import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ShoppingBag, ShoppingCart, Plus, Minus, Trash2, X,
  Loader2, CheckCircle, Truck, Store, CreditCard, Heart, Ruler,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { PRODUCTS, SHIPPING_FEE, WORN_PHOTOS } from '../data/products';
import { PhotoLightbox } from '../components/PhotoLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CART_KEY = 'enatShopCart';

const loadCart = () => {
  try {
    const cart = JSON.parse(localStorage.getItem(CART_KEY) || '{}');
    // Drop legacy entries missing required variants
    Object.keys(cart).forEach((key) => {
      const [id, size, color] = key.split('|');
      const product = PRODUCTS.find((p) => p.id === id);
      if (!product || (product.sizes && !size) || (product.colors && !color)) delete cart[key];
    });
    return cart;
  } catch {
    return {};
  }
};

const SIZE_GUIDE = [
  { size: 'S', bust: '82–88 cm', waist: '62–68 cm', hips: '88–94 cm', ca: '4–6' },
  { size: 'M', bust: '88–94 cm', waist: '68–74 cm', hips: '94–100 cm', ca: '8–10' },
  { size: 'L', bust: '94–100 cm', waist: '74–80 cm', hips: '100–106 cm', ca: '12–14' },
];

const SizeGuideDialog = ({ lang }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type="button"
        data-testid="shop-size-guide-btn"
        className="inline-flex items-center gap-1 text-xs font-medium text-[#4A708B] hover:text-[#3B5A6F] underline underline-offset-2 transition-colors"
      >
        <Ruler className="w-3.5 h-3.5" />
        {lang === 'fr' ? 'Guide des tailles' : 'Size guide'}
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-md rounded-3xl" data-testid="shop-size-guide-dialog">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-[#1A252C]">
          {lang === 'fr' ? 'Guide des tailles' : 'Size Guide'}
        </DialogTitle>
      </DialogHeader>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E2E8F0] text-[#546A76]">
              <th className="py-2.5 px-2 text-left font-semibold">{lang === 'fr' ? 'Taille' : 'Size'}</th>
              <th className="py-2.5 px-2 text-left font-semibold">{lang === 'fr' ? 'Buste' : 'Bust'}</th>
              <th className="py-2.5 px-2 text-left font-semibold">{lang === 'fr' ? 'Taille (tour)' : 'Waist'}</th>
              <th className="py-2.5 px-2 text-left font-semibold">{lang === 'fr' ? 'Hanches' : 'Hips'}</th>
              <th className="py-2.5 px-2 text-left font-semibold">{lang === 'fr' ? 'Taille CA' : 'CA size'}</th>
            </tr>
          </thead>
          <tbody>
            {SIZE_GUIDE.map((row) => (
              <tr key={row.size} className="border-b border-[#E2E8F0]/60 text-[#1A252C]">
                <td className="py-2.5 px-2">
                  <span className="inline-flex w-7 h-7 rounded-full bg-[#4A708B] text-white text-xs font-bold items-center justify-center">{row.size}</span>
                </td>
                <td className="py-2.5 px-2">{row.bust}</td>
                <td className="py-2.5 px-2">{row.waist}</td>
                <td className="py-2.5 px-2">{row.hips}</td>
                <td className="py-2.5 px-2">{row.ca}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#546A76] leading-relaxed">
        {lang === 'fr'
          ? 'Le tissu est extensible et épouse la silhouette. Entre deux tailles ? Choisissez la plus grande pour un confort optimal, ou la plus petite pour un effet plus ajusté.'
          : 'The fabric is stretchy and hugs the silhouette. Between two sizes? Choose the larger for optimal comfort, or the smaller for a more fitted look.'}
      </p>
    </DialogContent>
  </Dialog>
);

const ProductCard = ({ product, lang, quantity, onAdd }) => {
  const [size, setSize] = useState(product.sizes ? product.sizes[1] : null);
  const [color, setColor] = useState(product.colors ? product.colors[0] : null);
  const [imgIndex, setImgIndex] = useState(0);
  const hasSizes = !!product.sizes;
  const images = color ? color.images : [product.image];
  const displayImage = images[Math.min(imgIndex, images.length - 1)];

  const selectColor = (c) => {
    setColor(c);
    setImgIndex(0);
  };

  return (
  <div
    data-testid={`shop-product-${product.id}`}
    className="bg-white rounded-3xl border border-[#E2E8F0] shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
  >
    <div className="aspect-square bg-[#FAFAFA] overflow-hidden relative">
      <img
        src={displayImage}
        alt={product.name[lang]}
        loading="lazy"
        data-testid={`shop-image-${product.id}`}
        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
      />
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((img, i) => (
            <button
              key={img}
              type="button"
              onClick={() => setImgIndex(i)}
              aria-label={i === 0 ? (lang === 'fr' ? 'Vue avant' : 'Front view') : (lang === 'fr' ? 'Vue arrière' : 'Back view')}
              data-testid={`shop-view-${product.id}-${i}`}
              className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm transition-colors ${
                imgIndex === i ? 'bg-[#4A708B] text-white' : 'bg-white/80 text-[#1A252C] hover:bg-white'
              }`}
            >
              {i === 0 ? (lang === 'fr' ? 'Avant' : 'Front') : (lang === 'fr' ? 'Arrière' : 'Back')}
            </button>
          ))}
        </div>
      )}
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-serif text-xl font-bold text-[#1A252C] mb-2">{product.name[lang]}</h3>
      <p className="text-[#546A76] text-sm leading-relaxed mb-4 flex-1">{product.description[lang]}</p>
      {product.colors && (
        <div className="mb-4">
          <p className="text-xs font-medium text-[#546A76] uppercase tracking-wide mb-2">
            {lang === 'fr' ? 'Couleur' : 'Color'} : <span className="text-[#1A252C] normal-case">{color.label[lang]}</span>
          </p>
          <div className="flex gap-2.5">
            {product.colors.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => selectColor(c)}
                aria-label={c.label[lang]}
                data-testid={`shop-color-${product.id}-${c.id}`}
                className={`w-9 h-9 rounded-full border-2 transition-all ${
                  color.id === c.id ? 'border-[#4A708B] ring-2 ring-[#4A708B]/30 scale-110' : 'border-[#E2E8F0] hover:border-[#4A708B]/50'
                }`}
                style={{ backgroundColor: c.swatch }}
              />
            ))}
          </div>
        </div>
      )}
      {hasSizes && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-[#546A76] uppercase tracking-wide">
              {lang === 'fr' ? 'Taille' : 'Size'}
            </p>
            <SizeGuideDialog lang={lang} />
          </div>
          <div className="flex gap-2">
            {product.sizes.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSize(s)}
                data-testid={`shop-size-${product.id}-${s}`}
                className={`w-10 h-10 rounded-full border-2 text-sm font-semibold transition-colors ${
                  size === s
                    ? 'border-[#4A708B] bg-[#4A708B] text-white'
                    : 'border-[#E2E8F0] text-[#546A76] hover:border-[#4A708B]/50'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-2xl font-bold text-[#4A708B]" data-testid={`shop-price-${product.id}`}>
          {product.price}$ <span className="text-sm font-medium text-[#546A76]">CAD</span>
        </span>
        <Button
          onClick={() => onAdd(product.id, size, color ? color.id : null)}
          data-testid={`shop-add-${product.id}`}
          className="gold-btn rounded-full px-5 relative"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          {lang === 'fr' ? 'Ajouter' : 'Add'}
          {quantity > 0 && (
            <span className="absolute -top-2 -right-2 bg-[#1A252C] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid={`shop-qty-${product.id}`}>
              {quantity}
            </span>
          )}
        </Button>
      </div>
    </div>
  </div>
  );
};

export default function ShopPage() {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'fr';
  const [searchParams, setSearchParams] = useSearchParams();
  const [cart, setCart] = useState(loadCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [delivery, setDelivery] = useState('pickup');
  const [customer, setCustomer] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [wornLightbox, setWornLightbox] = useState(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const sessionId = searchParams.get('stripe_session_id');
    const cancelled = searchParams.get('stripe_cancelled');
    if (cancelled === 'true') {
      toast.error(lang === 'fr' ? 'Paiement annulé' : 'Payment cancelled');
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sessionId || hasProcessedRef.current) return;
    hasProcessedRef.current = true;
    setSearchParams({}, { replace: true });
    setIsVerifying(true);

    const poll = async () => {
      for (let i = 0; i < 12; i++) {
        try {
          const res = await axios.get(`${API}/shop/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            setOrderSuccess(res.data);
            setCart({});
            localStorage.removeItem(CART_KEY);
            setIsVerifying(false);
            toast.success(lang === 'fr' ? 'Merci pour votre commande!' : 'Thank you for your order!');
            return;
          }
          if (['failed', 'expired'].includes(res.data.payment_status)) {
            toast.error(lang === 'fr' ? "Le paiement n'a pas pu être complété." : 'The payment could not be completed.');
            setIsVerifying(false);
            return;
          }
        } catch {
          // retry
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      setIsVerifying(false);
      toast.error(lang === 'fr'
        ? 'La vérification prend plus de temps que prévu. Vérifiez votre courriel pour la confirmation.'
        : 'Verification is taking longer than expected. Please check your email for confirmation.');
    };
    poll();
  }, [searchParams, setSearchParams, lang]);

  const cartKey = (id, size, color) => [id, size || '', color || ''].join('|');
  const parseKey = (key) => {
    const [id, size, color] = key.split('|');
    return { id, size: size || null, color: color || null };
  };
  const addItem = (id, size = null, color = null) => setCart((c) => {
    const k = cartKey(id, size, color);
    return { ...c, [k]: (c[k] || 0) + 1 };
  });
  const removeItem = (id, size = null, color = null) => setCart((c) => {
    const k = cartKey(id, size, color);
    const next = { ...c };
    if ((next[k] || 0) <= 1) delete next[k];
    else next[k] -= 1;
    return next;
  });
  const deleteItem = (id, size = null, color = null) => setCart((c) => {
    const next = { ...c };
    delete next[cartKey(id, size, color)];
    return next;
  });

  const cartEntries = useMemo(
    () => Object.entries(cart)
      .map(([key, qty]) => {
        const { id, size, color } = parseKey(key);
        const product = PRODUCTS.find((p) => p.id === id);
        const colorObj = product?.colors?.find((c) => c.id === color) || null;
        return { key, product, size, color, colorObj, qty };
      })
      .filter((e) => e.product && e.qty > 0),
    [cart]
  );
  const productQty = (id) => cartEntries.filter((e) => e.product.id === id).reduce((s, e) => s + e.qty, 0);
  const itemCount = cartEntries.reduce((s, e) => s + e.qty, 0);
  const subtotal = cartEntries.reduce((s, e) => s + e.product.price * e.qty, 0);
  const shippingCost = delivery === 'shipping' ? SHIPPING_FEE : 0;
  const total = subtotal + shippingCost;

  const handleCheckout = async () => {
    if (!customer.name.trim() || !customer.email.trim()) {
      toast.error(lang === 'fr' ? 'Veuillez entrer votre nom et courriel.' : 'Please enter your name and email.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/shop/checkout`, {
        items: cartEntries.map((e) => ({ product_id: e.product.id, quantity: e.qty, size: e.size, color: e.color })),
        customer_name: customer.name.trim(),
        email: customer.email.trim(),
        delivery_method: delivery,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Une erreur est survenue.' : 'An error occurred.'));
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="shop-verifying">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#4A708B] animate-spin mx-auto mb-4" />
          <p className="text-[#546A76]">{lang === 'fr' ? 'Vérification de votre paiement...' : 'Verifying your payment...'}</p>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4 pt-24 pb-16" data-testid="shop-order-success">
        <div className="bg-white rounded-3xl shadow-sm border border-[#E2E8F0] p-10 max-w-lg w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h1 className="font-serif text-3xl font-bold text-[#1A252C] mb-3">
            {lang === 'fr' ? 'Merci pour votre commande!' : 'Thank you for your order!'}
          </h1>
          <p className="text-[#546A76] mb-6">
            {lang === 'fr'
              ? 'Votre paiement a été reçu. Une confirmation vous a été envoyée par courriel.'
              : 'Your payment was received. A confirmation email has been sent to you.'}
          </p>
          <div className="bg-[#FAFAFA] rounded-2xl p-6 text-left space-y-2 mb-8">
            <p className="text-sm text-[#546A76]">
              <strong>{lang === 'fr' ? 'Commande' : 'Order'}:</strong> {orderSuccess.order_number}
            </p>
            {(orderSuccess.items || []).map((it) => (
              <p key={it.product_id} className="text-sm text-[#546A76]">
                {it.quantity}x {lang === 'fr' ? it.name_fr : it.name_en} — {it.price * it.quantity}$
              </p>
            ))}
            <p className="text-sm text-[#546A76]">
              <strong>{lang === 'fr' ? 'Livraison' : 'Delivery'}:</strong>{' '}
              {orderSuccess.delivery_method === 'shipping'
                ? (lang === 'fr' ? 'Livraison' : 'Shipping')
                : (lang === 'fr' ? 'Retrait en personne' : 'Pickup')}
            </p>
            <p className="text-[#1A252C] font-bold">
              Total: {orderSuccess.total}$ CAD
            </p>
          </div>
          <Button onClick={() => setOrderSuccess(null)} className="gold-btn rounded-full px-8" data-testid="shop-back-to-shop">
            {lang === 'fr' ? 'Retour à la boutique' : 'Back to shop'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="shop-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
            {lang === 'fr' ? 'Boutique solidaire' : 'Charity Shop'}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A252C] mb-6">
            {lang === 'fr' ? 'Notre boutique' : 'Our Shop'}
          </h1>
          <p className="text-[#546A76] text-lg max-w-2xl">
            {lang === 'fr'
              ? 'Chaque achat soutient directement notre mission auprès des femmes et enfants victimes de violence au Québec.'
              : 'Every purchase directly supports our mission helping women and children victims of violence in Quebec.'}
          </p>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {PRODUCTS.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                lang={lang}
                quantity={productQty(p.id)}
                onAdd={addItem}
                onRemove={removeItem}
              />
            ))}
          </div>
          <div className="mt-12 flex items-center gap-3 text-[#546A76] bg-[#4A708B]/5 rounded-2xl p-5">
            <Heart className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
            <p className="text-sm">
              {lang === 'fr'
                ? `Retrait en personne gratuit à Montréal, ou livraison au Québec pour ${SHIPPING_FEE}$ fixes. Les profits financent nos services d'accompagnement.`
                : `Free pickup in Montreal, or flat ${SHIPPING_FEE}$ shipping within Quebec. Profits fund our support services.`}
            </p>
          </div>
        </div>
      </section>

      {/* Worn by clients */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="shop-worn-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
            {lang === 'fr' ? 'L\'effet réel' : 'The real look'}
          </p>
          <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-3">
            {lang === 'fr' ? 'Portée par nos clientes' : 'Worn by our clients'}
          </h2>
          <p className="text-[#546A76] mb-10 max-w-2xl">
            {lang === 'fr'
              ? 'Découvrez le rendu de la combinaison au quotidien, portée par de vraies clientes.'
              : 'See how the jumpsuit really looks, worn by real clients.'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {WORN_PHOTOS.map((photo, i) => (
              <button
                key={photo}
                type="button"
                onClick={() => setWornLightbox(i)}
                aria-label={`${lang === 'fr' ? 'Agrandir la photo' : 'Enlarge photo'} ${i + 1}`}
                data-testid={`shop-worn-photo-${i}`}
                className="cursor-zoom-in"
              >
                <img
                  src={photo}
                  alt={`${lang === 'fr' ? 'Combinaison portée' : 'Jumpsuit worn'} ${i + 1}`}
                  loading="lazy"
                  className="rounded-2xl object-cover object-top w-full h-64 sm:h-80 hover:opacity-90 transition-opacity"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {wornLightbox !== null && (
        <PhotoLightbox
          photos={WORN_PHOTOS}
          index={wornLightbox}
          title={lang === 'fr' ? 'Combinaison portée par nos clientes' : 'Jumpsuit worn by our clients'}
          onClose={() => setWornLightbox(null)}
          onNavigate={(i) => setWornLightbox(i)}
        />
      )}

      {/* Floating cart button */}
      {itemCount > 0 && !cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          data-testid="shop-cart-button"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-3 gold-btn rounded-full px-6 py-4 shadow-xl font-medium"
        >
          <ShoppingBag className="w-5 h-5" />
          {lang === 'fr' ? 'Panier' : 'Cart'}
          <span className="bg-[#1A252C] text-white text-xs rounded-full w-6 h-6 flex items-center justify-center" data-testid="shop-cart-count">
            {itemCount}
          </span>
        </button>
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50" data-testid="shop-cart-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
              <h2 className="font-serif text-2xl font-bold text-[#1A252C]">
                {lang === 'fr' ? 'Votre panier' : 'Your Cart'}
              </h2>
              <button
                type="button"
                onClick={() => setCartOpen(false)}
                aria-label="Fermer le panier"
                data-testid="shop-cart-close"
                className="w-10 h-10 rounded-full hover:bg-[#4A708B]/10 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-[#1A252C]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {cartEntries.length === 0 && (
                <p className="text-[#546A76] text-center py-10" data-testid="shop-cart-empty">
                  {lang === 'fr' ? 'Votre panier est vide.' : 'Your cart is empty.'}
                </p>
              )}
              {cartEntries.map(({ key, product, size, colorObj, qty }) => (
                <div key={key} className="flex gap-4 items-center" data-testid={`shop-cart-item-${key.replace(/\|/g, '-')}`}>
                  <img src={colorObj ? colorObj.images[0] : product.image} alt={product.name[lang]} className="w-16 h-16 rounded-xl object-cover object-top" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A252C] text-sm truncate">
                      {product.name[lang]}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {colorObj && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#4A708B]/10 text-[#4A708B] text-xs font-semibold">
                          <span className="w-2.5 h-2.5 rounded-full inline-block border border-black/10" style={{ backgroundColor: colorObj.swatch }} />
                          {colorObj.label[lang]}
                        </span>
                      )}
                      {size && <span className="px-1.5 py-0.5 rounded bg-[#4A708B]/10 text-[#4A708B] text-xs font-semibold">{size}</span>}
                      <span className="text-[#546A76] text-xs">{product.price}$ CAD</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => removeItem(product.id, size, colorObj?.id)} aria-label="Réduire" className="w-7 h-7 rounded-full bg-[#4A708B]/10 text-[#4A708B] flex items-center justify-center">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-5 text-center text-sm font-semibold">{qty}</span>
                    <button type="button" onClick={() => addItem(product.id, size, colorObj?.id)} aria-label="Augmenter" className="w-7 h-7 rounded-full bg-[#4A708B] text-white flex items-center justify-center">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button type="button" onClick={() => deleteItem(product.id, size, colorObj?.id)} aria-label="Supprimer" data-testid={`shop-cart-remove-${key.replace(/\|/g, '-')}`} className="text-[#546A76] hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {cartEntries.length > 0 && (
              <div className="border-t border-[#E2E8F0] p-6 space-y-5">
                {/* Delivery choice */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setDelivery('pickup')}
                    data-testid="shop-delivery-pickup"
                    className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-4 text-sm transition-colors ${
                      delivery === 'pickup' ? 'border-[#4A708B] bg-[#4A708B]/5 text-[#4A708B]' : 'border-[#E2E8F0] text-[#546A76]'
                    }`}
                  >
                    <Store className="w-5 h-5" />
                    {lang === 'fr' ? 'Retrait (gratuit)' : 'Pickup (free)'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDelivery('shipping')}
                    data-testid="shop-delivery-shipping"
                    className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-4 text-sm transition-colors ${
                      delivery === 'shipping' ? 'border-[#4A708B] bg-[#4A708B]/5 text-[#4A708B]' : 'border-[#E2E8F0] text-[#546A76]'
                    }`}
                  >
                    <Truck className="w-5 h-5" />
                    {lang === 'fr' ? `Livraison (+${SHIPPING_FEE}$)` : `Shipping (+$${SHIPPING_FEE})`}
                  </button>
                </div>

                {/* Customer info */}
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="shop-name" className="text-sm text-[#1A252C]">{lang === 'fr' ? 'Nom complet' : 'Full name'}</Label>
                    <Input
                      id="shop-name"
                      data-testid="shop-customer-name"
                      value={customer.name}
                      onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                      placeholder={lang === 'fr' ? 'Votre nom' : 'Your name'}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shop-email" className="text-sm text-[#1A252C]">{lang === 'fr' ? 'Courriel' : 'Email'}</Label>
                    <Input
                      id="shop-email"
                      type="email"
                      data-testid="shop-customer-email"
                      value={customer.email}
                      onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                      placeholder={lang === 'fr' ? 'votre@courriel.com' : 'your@email.com'}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-[#546A76]">
                    <span>{lang === 'fr' ? 'Sous-total' : 'Subtotal'}</span>
                    <span data-testid="shop-subtotal">{subtotal}$ CAD</span>
                  </div>
                  <div className="flex justify-between text-[#546A76]">
                    <span>{lang === 'fr' ? 'Livraison' : 'Shipping'}</span>
                    <span data-testid="shop-shipping">{shippingCost}$ CAD</span>
                  </div>
                  <div className="flex justify-between text-[#1A252C] font-bold text-base pt-1.5 border-t border-[#E2E8F0]">
                    <span>Total</span>
                    <span data-testid="shop-total">{total}$ CAD</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={isSubmitting}
                  data-testid="shop-checkout-btn"
                  className="w-full gold-btn rounded-full py-6 text-base"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      {lang === 'fr' ? 'Payer avec Stripe' : 'Pay with Stripe'}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
